import {
    SURVEY_FROM,
    GOOGLE_PARAMS,
    FACEBOOK_PARAMS,
    PINTEREST_PARAMS,
    TIKTOK_PARAMS,
    APPLOVIN_PARAMS,
    TWITTER_PARAMS,
    REDDIT_PARAMS,
    SNAPCHAT_PARAMS
} from "../const/LocalStorage";
import {getQueryString} from "../utils/common";

// Ad source configuration
const adSourceConfig = {
    google: {
        key: "google",
        storageKey: GOOGLE_PARAMS,
        params: [
            "gclid",
            "utm_source",
            "campaign",
            "adgroup",
            "creative",
            "utm_term",
            "network",
            "device",
            "placement",
        ],
        getAdInfo: () => {
            return getAdParams("google");
        },
    },
    facebook: {
        key: "facebook",
        storageKey: FACEBOOK_PARAMS,
        params: [
            "campaign_id",
            "adset_id",
            "ad_id",
            "utm_source",
            "campaign_name",
            "adset_name",
            "ad_name",
        ],
        getAdInfo: () => {
            return getAdParams("facebook");
        },
    },
    pinterest: {
        key: "pinterest",
        storageKey: PINTEREST_PARAMS,
        params: ["utm_source", "campaign", "adgroup", "creative"],
        getAdInfo: () => {
            return getAdParams("pinterest");
        },
    },
    tiktok: {
        key: "tiktok",
        storageKey: TIKTOK_PARAMS,
        params: [
            "utm_source",
            "campaign",
            "adgroup",
            "creative",
            "adgroup_id",
            "campaign_id",
            "creative_id",
        ],
        getAdInfo: () => {
            return getAdParams("tiktok");
        },
    },
    applovin: {
        key: "applovin",
        storageKey: APPLOVIN_PARAMS,
        params: ["utm_source", "utm_medium", "utm_campaign", "utm_content"],
        getAdInfo: () => {
            return getAdParams("applovin");
        },
    },
    twitter: {
        key: "twitter",
        storageKey: TWITTER_PARAMS,
        params: [
            "utm_source",
            "utm_campaign",
            "utm_medium",
            "utm_content",
            "twclid",
        ],
        getAdInfo: () => {
            return getAdParams("twitter");
        },
    },
    reddit: {
        key: "reddit",
        storageKey: REDDIT_PARAMS,
        params: ["utm_source", "utm_campaign"],
        getAdInfo: () => {
            return getAdParams("reddit");
        },
    },
    snapchat: {
        key: "snapchat",
        storageKey: SNAPCHAT_PARAMS,
        params: ["utm_source", "utm_campaign"],
        getAdInfo: () => {
            return getAdParams("snapchat");
        },
    },
    organic: {
        key: "organic",
        storageKey: null,
        params: [],
        getAdInfo: () => {
            return "";
        },
    },
};

/**
 * Get ad source type from URL parameters or localStorage
 * Priority:
 * 1. Check utm_source parameter
 * 2. Check platform-specific parameters (gclid, did, etc.)
 * 3. Check platform-specific parameter combinations
 * 4. Check localStorage for recent ad sources (within 7 days)
 * 5. Return "organic" if no source found
 * @returns {string} Ad source key (google, facebook, organic, etc.)
 */
export function getFromType() {
    // 1. Check utm_source parameter first
    const utmSource = getQueryString("utm_source");
    if (utmSource) {
        const source = utmSource.toLowerCase();
        // Check if source is a known ad platform key
        if (Object.values(adSourceConfig).some((config) => config.key === source)) {
            return source;
        }
    }

    // 2. Check platform-specific parameters for legacy ads without utm_source
    const gclid = getQueryString("gclid");
    if (gclid && gclid.length > 0) {
        return adSourceConfig.google.key;
    }

    // AppLovin
    const did = getQueryString("did");
    if (did && did.length > 0) {
        return adSourceConfig.applovin.key;
    }

    // 3. Check platform-specific parameter combinations
    // Facebook specific parameters
    const fbAdId = getQueryString("ad_id");
    const fbAdsetId = getQueryString("adset_id");
    if ((fbAdId && fbAdId.length > 0) || (fbAdsetId && fbAdsetId.length > 0)) {
        return adSourceConfig.facebook.key;
    }

    // TikTok specific parameters
    const ttAdgroupId = getQueryString("adgroup_id");
    const ttCreativeId = getQueryString("creative_id");
    if (
        (ttAdgroupId && ttAdgroupId.length > 0) ||
        (ttCreativeId && ttCreativeId.length > 0)
    ) {
        return adSourceConfig.tiktok.key;
    }

    // 4. Check localStorage for recent ad sources (within 7 days)
    const now = new Date().getTime();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

    let latestSource = null;
    let latestTimestamp = 0;

    // Check each ad source for the most recent valid record
    for (const [sourceKey, config] of Object.entries(adSourceConfig)) {
        // Skip sources without storage key
        if (!config.storageKey) continue;

        const paramsJson = localStorage.getItem(config.storageKey);
        if (paramsJson && paramsJson.length > 0) {
            try {
                const params = JSON.parse(paramsJson);
                const timestamp = params.timestamp;

                // Check if within 7 days and is newer than current latest
                if (
                    timestamp &&
                    now - timestamp <= sevenDaysMs &&
                    timestamp > latestTimestamp
                ) {
                    latestTimestamp = timestamp;
                    latestSource = config.key;
                }
            } catch (e) {
                // JSON parse failed, skip this entry
                console.error("Failed to parse JSON for " + config.storageKey);
            }
        }
    }

    // If found valid source record, return it
    if (latestSource) {
        return latestSource;
    }

    // If no valid source found, return organic
    return adSourceConfig.organic.key;
}

/**
 * Get ad info for tracking
 * Returns the ad info JSON string for the detected source
 * @returns {string} Ad info JSON string or empty string
 */
export function getAdInfo() {
    const sourceKey = getFromType();
    if (sourceKey && adSourceConfig[sourceKey]) {
        return adSourceConfig[sourceKey].getAdInfo();
    }
    return "";
}

/**
 * Get ad parameters for a specific source
 * @param {string} sourceKey - Ad source key (google, facebook, etc.)
 * @returns {string} JSON string of ad parameters or empty string
 */
function getAdParams(sourceKey) {
    const config = adSourceConfig[sourceKey];
    if (!config || !config.storageKey) {
        return "";
    }

    // Get parameters from localStorage
    let paramsJson = localStorage.getItem(config.storageKey);
    let params = {};
    try {
        params = paramsJson ? JSON.parse(paramsJson) : {};
    } catch (e) {
        params = {};
    }

    // Check if URL has ad parameters and update
    let hasNewParams = false;
    config.params.forEach((param) => {
        hasNewParams = updateWithQueryString({ obj: params, key: param }) || hasNewParams;
    });

    const hasParams = Object.keys(params).some((key) => params[key]);

    if (!hasParams) {
        return "";
    }

    params.from = config.key;

    // Only update timestamp if URL has new ad parameters
    if (hasNewParams) {
        params.timestamp = new Date().getTime();
    }

    localStorage.setItem(SURVEY_FROM, config.key);
    let json = JSON.stringify(params);
    localStorage.setItem(config.storageKey, json);
    return json;
}

/**
 * Update object with query string parameter
 * @param {Object} options - Options object
 * @param {Object} options.obj - Object to update
 * @param {string} options.key - Parameter key to check in URL
 * @returns {boolean} True if parameter found in URL, false otherwise
 */
function updateWithQueryString({ obj, key }) {
    let value = getQueryString(key);
    if (value && value.length > 0) {
        obj[key] = value;
        return true; // Found URL parameter
    }
    return false; // No URL parameter found
}
